import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Layout, Steps, Card, message, ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import { FileOutlined, DatabaseOutlined, LinkOutlined, SendOutlined, SettingOutlined } from '@ant-design/icons';
import FileUpload from './components/FileUpload';
import TemplateSelection from './components/TemplateSelection';
import ColumnMapping from './components/ColumnMapping';
import PassthroughConfig from './components/PassthroughConfig';
import FileProcessing from './components/FileProcessing';
import ProtectedRoute from './components/ProtectedRoute';
import UserInfo from './components/UserInfo';
import WidgetTestPage from './components/WidgetTestPage';
import DevApp from './components/DevApp';
import { api, setAuthToken, setAuthInstance } from './services/api';
import { DEV_MODE, DISABLE_AUTH } from './auth/authConfig';
import './index.css';

const { Header, Content } = Layout;
const { Step } = Steps;

function App() {
    const auth = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [fileColumns, setFileColumns] = useState([]);
    const [templates, setTemplates] = useState({});
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [columnMappings, setColumnMappings] = useState([]);
    const [passthroughEnabled, setPassthroughEnabled] = useState(false);
    const [, setPassthroughConfig] = useState(null);

    // Set auth instance for API interceptor
    useEffect(() => {
        if (auth) {
            setAuthInstance(auth);
        }
    }, [auth]);

    // Set auth token when user is authenticated
    // Use access_token for API calls (ingress validates this token)
    useEffect(() => {
        if (auth.isAuthenticated && auth.user?.access_token) {
            setAuthToken(auth.user.access_token);
            loadTemplates();
        }
    }, [auth.isAuthenticated, auth.user]);

    // Monitor authentication errors (e.g., token expiration)
    useEffect(() => {
        if (auth.error) {
            console.error('Authentication error:', auth.error);

            // Check if error is related to token expiration
            if (auth.error.message?.includes('expired') ||
                auth.error.message?.includes('invalid_token')) {
                message.error('Your session has expired. Please log in again.');
                // The ProtectedRoute will handle redirect to login
            }
        }
    }, [auth.error]);

    // Check if we're in dev mode with disabled auth
    if (DEV_MODE || DISABLE_AUTH) {
        return <DevApp />;
    }

    const loadTemplates = async () => {
        try {
            const response = await api.get('/templates');
            setTemplates(response.data.templates);
            setPassthroughEnabled(response.data.passthrough_enabled || false);
            if (response.data.passthrough_config) {
                setPassthroughConfig(response.data.passthrough_config);
            }
        } catch (error) {
            message.error('Failed to load templates configuration');
        }
    };

    const handleFileUpload = (file, preview, columns) => {
        setUploadedFile(file);
        setFilePreview(preview);
        setFileColumns(columns);
        setCurrentStep(1);
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        // If passthrough mode, skip to step 2 (passthrough config)
        // Otherwise go to column mapping (step 2)
        setCurrentStep(2);
    };

    const handleMappingComplete = (mappings) => {
        setColumnMappings(mappings);
        setCurrentStep(3);
    };

    const handlePassthroughConfigComplete = (config) => {
        // Store passthrough config as column mappings format
        setColumnMappings(config);
        setCurrentStep(3);
    };

    const handleProcessingComplete = (outputFile) => {
        // Don't set processedFile to avoid component re-render
    };

    const handleReset = () => {
        setCurrentStep(0);
        setUploadedFile(null);
        setFilePreview(null);
        setFileColumns([]);
        setSelectedTemplate(null);
        setColumnMappings([]);
        // Note: Don't reset passthroughEnabled as it's tenant-level setting
    };

    // Dynamic steps based on whether passthrough mode is selected
    const isPassthroughMode = selectedTemplate?.isPassthrough === true;

    const steps = [
        {
            title: 'Upload File',
            icon: <FileOutlined />,
        },
        {
            title: 'Select Template',
            icon: <DatabaseOutlined />,
        },
        {
            title: isPassthroughMode ? 'Configure' : 'Map Columns',
            icon: isPassthroughMode ? <SettingOutlined /> : <LinkOutlined />,
        },
        {
            title: 'Process & Send',
            icon: <SendOutlined />,
        },
    ];

    const MainApp = () => (
        <Layout style={{ minHeight: '100vh' }}>
            <Header style={{
                background: '#fff',
                padding: '0 50px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileOutlined style={{ fontSize: 32, color: '#1890ff', marginRight: 16 }} />
                    <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
                        File Uploader
                    </h1>
                </div>
                <UserInfo />
            </Header>

            <Content style={{ padding: '50px', background: '#f0f2f5' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                    <Card style={{ marginBottom: 24 }}>
                        <Steps current={currentStep} style={{ marginBottom: 24 }}>
                            {steps.map((step, index) => (
                                <Step
                                    key={index}
                                    title={step.title}
                                    icon={step.icon}
                                />
                            ))}
                        </Steps>
                    </Card>

                    <Card>
                        {currentStep === 0 && (
                            <FileUpload
                                onFileUpload={handleFileUpload}
                                filePreview={filePreview}
                                fileColumns={fileColumns}
                            />
                        )}

                        {currentStep === 1 && (
                            <TemplateSelection
                                templates={templates}
                                selectedTemplate={selectedTemplate}
                                onTemplateSelect={handleTemplateSelect}
                                passthroughEnabled={passthroughEnabled}
                            />
                        )}

                        {currentStep === 2 && !isPassthroughMode && (
                            <ColumnMapping
                                selectedTemplate={selectedTemplate}
                                fileColumns={fileColumns}
                                onMappingComplete={handleMappingComplete}
                            />
                        )}

                        {currentStep === 2 && isPassthroughMode && (
                            <PassthroughConfig
                                fileColumns={fileColumns}
                                originalFilename={uploadedFile?.name}
                                onConfigComplete={handlePassthroughConfigComplete}
                            />
                        )}

                        {currentStep === 3 && (
                            <FileProcessing
                                uploadedFile={uploadedFile}
                                selectedTemplate={selectedTemplate}
                                columnMappings={columnMappings}
                                onProcessingComplete={handleProcessingComplete}
                                onReset={handleReset}
                            />
                        )}
                    </Card>
                </div>
            </Content>
        </Layout>
    );

    return (
        <ConfigProvider
            locale={enUS}
            theme={{
                token: {
                    colorPrimary: '#1890ff',
                },
            }}
        >
            <Routes>
                <Route path="/widget-test" element={
                    <ProtectedRoute>
                        <WidgetTestPage />
                    </ProtectedRoute>
                } />
                <Route path="/" element={
                    <ProtectedRoute>
                        <MainApp />
                    </ProtectedRoute>
                } />
            </Routes>
        </ConfigProvider>
    );
}

export default App;


